<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required } from '@vuelidate/validators';
import router from '../../../../index';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { isPhoneE164OrEmpty } from 'shared/helpers/Validators';
import axios from 'axios';

export default {
  components: {
    NextButton,
    Spinner,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      inboxName: '',
      phoneNumber: '',
      apiKey: '',
      accountId: '',
      isLinking: false,
      qrCodeUrl: '',
      pollInterval: null,
      linkToken: '',
    };
  },
  computed: {
    ...mapGetters({ uiFlags: 'inboxes/getUIFlags' }),
    apiUrl() {
      return (
        window.aidexaConfig.whatsappSelfhostedUrl ||
        'https://messager.mailinci.com/api'
      );
    },
  },
  validations: {
    inboxName: { required },
    phoneNumber: { required, isPhoneE164OrEmpty },
    apiKey: { required },
    accountId: { required },
  },
  beforeUnmount() {
    this.stopPolling();
  },
  methods: {
    async linkAccount() {
      if (!this.apiKey) {
        useAlert(this.$t('INBOX_MGMT.ADD.WHATSAPP.API.API_KEY_REQUIRED'));
        return;
      }
      this.isLinking = true;
      this.qrCodeUrl = '';
      this.stopPolling();

      try {
        const response = await axios.get(
          `${this.apiUrl}/create/wa.link?secret=${this.apiKey}`
        );
        if (response.data && response.data.status === 200) {
          const { qrimagelink, infolink } = response.data.data;
          this.qrCodeUrl = qrimagelink;

          // Extract token from infolink if needed, or just use infolink url
          // infolink: ".../api/get/wa.info?token=..."
          // We can just poll this URL.
          this.startPolling(infolink);
        } else {
          useAlert(response.data.message || 'Failed to create link');
          this.isLinking = false;
        }
      } catch (error) {
        useAlert(error.message || 'Error linking account');
        this.isLinking = false;
      }
    },
    startPolling(url) {
      this.pollInterval = setInterval(async () => {
        try {
          const response = await axios.get(url);
          // Assuming successful response structure contains phone/account info when linked
          // The doc says "Get WhatsApp information after linking".
          // If not linked yet, maybe it returns error or specific status.

          if (response.data && response.data.status === 200) {
            // Check if we have account info
            // Data structure not fully documented ("No sample"), but assuming it returns similar to validation
            // Let's assume response.data.data contains 'phone' and 'unique' (account id)
            if (response.data.data && response.data.data.phone) {
              this.phoneNumber = response.data.data.phone;
              this.accountId =
                response.data.data.unique || response.data.data.account; // Guessing field name
              this.stopPolling();
              this.isLinking = false;
              this.qrCodeUrl = ''; // connected
              useAlert(this.$t('INBOX_MGMT.ADD.WHATSAPP.API.LINK_SUCCESS'));
            }
          }
        } catch (error) {
          // Ignore polling errors or handle timeout
        }
      }, 3000);
    },
    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
    },
    async createChannel() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      try {
        const whatsappChannel = await this.$store.dispatch(
          'inboxes/createChannel',
          {
            name: this.inboxName?.trim(),
            channel: {
              type: 'whatsapp',
              phone_number: this.phoneNumber,
              provider: 'selfhosted',
              provider_config: {
                api_key: this.apiKey,
                account_id: this.accountId,
              },
            },
          }
        );

        router.replace({
          name: 'settings_inboxes_add_agents',
          params: {
            page: 'new',
            inbox_id: whatsappChannel.id,
          },
        });
      } catch (error) {
        useAlert(
          error.message || this.$t('INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div v-if="!qrCodeUrl && !accountId" class="flex gap-2 items-end">
      <div class="flex-grow">
        <label :class="{ error: v$.apiKey.$error }">
          <span>{{ $t('INBOX_MGMT.ADD.WHATSAPP.API_KEY.LABEL') }}</span>
          <input
            v-model="apiKey"
            type="text"
            :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.API_KEY.PLACEHOLDER')"
            @blur="v$.apiKey.$touch"
          />
          <span v-if="v$.apiKey.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.API_KEY.ERROR') }}
          </span>
        </label>
      </div>
      <div class="mb-3">
        <NextButton
          type="button"
          label="Link Account"
          :is-loading="isLinking"
          @click="linkAccount"
        />
      </div>
    </div>

    <div v-if="qrCodeUrl" class="p-4 border rounded bg-white text-center">
      <h3 class="mb-2">Scan QR Code</h3>
      <img :src="qrCodeUrl" class="mx-auto max-w-[200px]" />
      <p class="text-sm text-gray-500 mt-2">Waiting for connection...</p>
    </div>

    <form
      class="flex flex-wrap flex-col mx-0"
      @submit.prevent="createChannel()"
    >
      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.inboxName.$error }">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.LABEL') }}
          <input
            v-model="inboxName"
            type="text"
            :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.PLACEHOLDER')"
            @blur="v$.inboxName.$touch"
          />
          <span v-if="v$.inboxName.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.ERROR') }}
          </span>
        </label>
      </div>

      <div v-if="!qrCodeUrl" class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.apiKey.$error }">
          <span>{{ $t('INBOX_MGMT.ADD.WHATSAPP.API_KEY.LABEL') }}</span>
          <input
            v-model="apiKey"
            type="text"
            :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.API_KEY.PLACEHOLDER')"
            @blur="v$.apiKey.$touch"
          />
          <span v-if="v$.apiKey.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.API_KEY.ERROR') }}
          </span>
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.accountId.$error }">
          Unique ID (Account ID)
          <input
            v-model="accountId"
            type="text"
            placeholder="Account Unique ID"
            @blur="v$.accountId.$touch"
          />
          <span v-if="v$.accountId.$error" class="message">
            Account ID is required
          </span>
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.phoneNumber.$error }">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.LABEL') }}
          <input
            v-model="phoneNumber"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.PLACEHOLDER')
            "
            @blur="v$.phoneNumber.$touch"
          />
          <span v-if="v$.phoneNumber.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <NextButton
          type="submit"
          :label="$t('INBOX_MGMT.ADD.WHATSAPP.SUBMIT_BUTTON')"
          :is-loading="uiFlags.isCreating"
        />
      </div>
    </form>
  </div>
</template>
